import { useConfirmCostEstimates } from "@/lib/queries/hooks";
import CollapseIcon from "~icons/local/collapse";
import ExpandIcon from "~icons/local/expand";
import Files from "./Files";
import Timeline from "./timeline/Timeline";
import Button from "@/lib/Button";
import { readyToQuote } from "@/lib/utils";
import useToPage from "@/lib/useToPage";
import useModal from "@/lib/modal/useModal";
import NoBid from "./NoBid";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import SortablePartTree, {
  SortablePartTreeRef,
} from "./partTree/SortablePartTree";
import Card from "@/lib/Card";
import { useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

export default function RFQ() {
  const { showModal, closeModal } = useModal();
  const { confirmCostEstimates } = useConfirmCostEstimates();
  const { toQuote, toSend } = useToPage();
  const quoteRevision = useGetCurrentQuoteRevision();
  const canEditQuoteRevision = quoteRevision?.can_edit;
  if (!quoteRevision) return null;

  return (
    <div className="grow h-full flex flex-col">
      <div className="grow p-2 flex flex-col gap-4 overflow-y-auto">
        <PartTree
          canEdit={!!canEditQuoteRevision}
          quoteRevision={quoteRevision}
        />
        <Files />
        <Timeline />
      </div>
      <div className="w-full flex gap-4 justify-end px-3 py-2">
        {canEditQuoteRevision &&
          ["new", "estimated"].includes(quoteRevision.status) && (
            <>
              <Button
                alert
                onClick={() =>
                  showModal(
                    <NoBid
                      quoteRevisionId={quoteRevision.id}
                      done={closeModal}
                    />,
                  )
                }
              >
                No Bid RFQ
              </Button>
              <Button
                disabled={!readyToQuote(quoteRevision)}
                primary
                onClick={() =>
                  confirmCostEstimates(
                    {
                      quoteRevisionId: quoteRevision.id,
                    },
                    { onSuccess: () => toQuote() },
                  )
                }
              >
                Create Quote
              </Button>
            </>
          )}
        {["quote_created", "sent_quote"].includes(quoteRevision.status) && (
          <Button primary onClick={() => toSend()}>
            View Quote
          </Button>
        )}
      </div>
    </div>
  );
}

function PartTree({
  quoteRevision,
  canEdit,
}: {
  quoteRevision: ReturnType<typeof useGetCurrentQuoteRevision>;
  canEdit: boolean;
}) {
  const [showAddPart, setShowAddPart] = useState(false);
  const partTreeRef = useRef<SortablePartTreeRef>(null);

  const parts = useMemo(
    () => quoteRevision?.requested_part_revisions || [],
    [quoteRevision],
  );

  return (
    <Card
      header={
        <div className="min-h-8 flex items-center">
          <div className="flex items-center text-sm font-bold w-full justify-between">
            <div className="mx-1 min-w-[31rem]">Part</div>
            <div className="flex items-center">
              <div className="w-36">Quantities</div>
              <div className="w-56">Engineering Drawing</div>
              <div className="w-40">CAD Model</div>
              <Button
                onClick={() => setShowAddPart(true)}
                disabled={showAddPart}
                className="font-normal"
                secondary
              >
                Add Part
              </Button>
            </div>
          </div>
        </div>
      }
    >
      {parts.some((part) => part.children.length > 0) && (
        <div className="flex items-center gap-2 mb-2">
          <CollapseIcon
            className="size-8 cursor-pointer hover:bg-slate-300 rounded-full p-2 outline-0"
            onClick={() =>
              partTreeRef.current?.expandOrCollapseAll({ expand: false })
            }
            data-tooltip-id="collapse-all-tooltip"
            data-tooltip-delay-show={500}
          />
          <Tooltip id="collapse-all-tooltip">
            <div className="text-xs">Collapse all parts</div>
          </Tooltip>
          <ExpandIcon
            className="size-8 cursor-pointer hover:bg-slate-300 rounded-full p-2 outline-0"
            onClick={() =>
              partTreeRef.current?.expandOrCollapseAll({ expand: true })
            }
            data-tooltip-id="expand-all-tooltip"
            data-tooltip-delay-show={500}
          />
          <Tooltip id="expand-all-tooltip">
            <div className="text-xs">Expand all parts</div>
          </Tooltip>
        </div>
      )}

      <SortablePartTree
        ref={partTreeRef}
        parts={parts}
        canEdit={canEdit}
        collapsible
        showAddPart={showAddPart}
        resetShowAddPart={() => setShowAddPart(false)}
      />
    </Card>
  );
}
